// Feature #288 — one person's skills profile (sm-manager view), with the
// peer-assessment entry point and open-review roll-up (Feature #286).
//
// Feature #309 — the "Current profile" table is replaced by the technical
// strength spine, labelled from the manager's point of view, not "You".
// Feature #355 — the core competency radar #309 also added is gone: core
// competencies are retired from Skills Management. Capability badges, which
// rendered beside it, now have their own block above the spine.
//
// Feature #326 — "Request assessment" asks a named colleague to assess this
// person, scoped to named skills. Outstanding requests are listed below the
// deltas with a Withdraw control, because a request nobody can withdraw is
// worse than one anybody can.
//
// Feature #344 — opening a review checks what is still in flight for this
// person first. A review resolves the deltas that exist at the moment it is
// convened (R5), so an assessment still in draft or still out with an
// assessor is very likely to raise more deltas on the same skills a day
// later. This is a WARNING with Proceed and Cancel, never a block: the
// manager may have every reason to press on, and it is their call.
//
// (The work item names ReviewsPending.tsx as the launch surface. That page
// only links here — the button that actually calls `createReview` is the one
// below, so the warning belongs here.)
//
// Feature #356 — this is the primary place a Skills Manager assigns a
// capability badge. The badges block gets `canManage` when the viewer is
// sm_manager / platform_admin and is not looking at themselves; the API
// refuses a self-assignment regardless, so the flag is a courtesy, not
// the control.
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '@/auth/useAuth'
import { UserRole } from '@/types'
import { PageHeader } from '@/components/ui/PageHeader'
import { formatDateTime } from '@/utils/dateFormat'
import {
  createReview,
  getAssessmentRequests,
  getOutstandingAssessments,
  getPersonProfile,
  revokeAssessmentRequest,
  SmAssessmentRequest,
  SmOutstandingAssessment,
  SmPersonProfile,
} from '@/api/sm'
import { AssessmentFeed } from './components/AssessmentFeed'
import { OpenDeltaBanner } from './components/OpenDeltaBanner'
import { CapabilityBadges } from './components/CapabilityBadges'
import { StrengthSpine } from './components/StrengthSpine'
import { RequestAssessmentDialog } from './components/RequestAssessmentDialog'

const buttonStyle: React.CSSProperties = {
  padding: '8px 16px',
  backgroundColor: 'var(--color-magenta)',
  color: 'var(--color-white)',
  border: 'none',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'var(--font-family)',
}

const secondaryButtonStyle: React.CSSProperties = {
  ...buttonStyle,
  backgroundColor: 'transparent',
  color: 'var(--color-dark-grey)',
  border: '1px solid var(--color-light-grey)',
}

const StaffDetail: React.FC = () => {
  const navigate = useNavigate()
  const { userId } = useParams<{ userId: string }>()
  const { user } = useAuth()
  const roles = user?.roles || []
  const canManageBadges =
    (roles.includes(UserRole.SmManager) || roles.includes(UserRole.PlatformAdmin)) &&
    !!userId &&
    userId !== user?.id
  const [profile, setProfile] = useState<SmPersonProfile | null>(null)
  const [selectedDeltas, setSelectedDeltas] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const [requests, setRequests] = useState<SmAssessmentRequest[]>([])
  const [showRequestDialog, setShowRequestDialog] = useState(false)
  const [busyRequest, setBusyRequest] = useState<string | null>(null)
  // Feature #344 — non-null while the manager is being shown what is still
  // in flight and deciding whether to proceed.
  const [outstanding, setOutstanding] = useState<SmOutstandingAssessment[] | null>(
    null
  )
  const [openingReview, setOpeningReview] = useState(false)

  const loadRequests = () => {
    if (!userId) return
    getAssessmentRequests(userId, 'pending')
      .then(setRequests)
      .catch(() => setRequests([]))
  }

  const loadProfile = () => {
    if (!userId) return
    getPersonProfile(userId)
      .then(setProfile)
      .catch((e) => setError(e?.message || 'Failed to load profile'))
  }

  useEffect(() => {
    if (!userId) return
    loadProfile()
    loadRequests()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId])

  const toggleDelta = (id: string) =>
    setSelectedDeltas((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    )

  // Actually creates the review. Reached either directly (nothing
  // outstanding) or from Proceed on the #344 warning.
  const openReview = async () => {
    if (!userId || selectedDeltas.length === 0) return
    setOpeningReview(true)
    try {
      const review = await createReview(userId, selectedDeltas)
      navigate(`/sm/reviews/${review.id}`)
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to open review')
      setOutstanding(null)
    } finally {
      setOpeningReview(false)
    }
  }

  const handleOpenReview = async () => {
    if (!userId || selectedDeltas.length === 0) return
    setError(null)
    let pending: SmOutstandingAssessment[] = []
    try {
      pending = (await getOutstandingAssessments(userId)).items
    } catch {
      // The warning is advisory. If the check itself fails, carrying on is
      // strictly better than blocking a review the manager is entitled to
      // open — the failure mode of a missing warning is a second review,
      // the failure mode of a false block is a meeting that cannot happen.
      pending = []
    }
    if (pending.length === 0) {
      await openReview()
      return
    }
    setOutstanding(pending)
  }

  const handleRevoke = async (request: SmAssessmentRequest) => {
    if (
      !window.confirm(
        `Withdraw the request for ${request.assessor_display_name} to assess ` +
          `${profile?.display_name}? They will be told it is no longer needed.`
      )
    )
      return
    setBusyRequest(request.id)
    try {
      await revokeAssessmentRequest(request.id)
      loadRequests()
    } catch (e: any) {
      setError(
        e?.response?.data?.detail || e?.message || 'Failed to withdraw the request'
      )
    } finally {
      setBusyRequest(null)
    }
  }

  return (
    <div>
      <PageHeader
        title={profile ? `Skills — ${profile.display_name}` : 'Skills profile'}
        actions={
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => setShowRequestDialog(true)}
              style={secondaryButtonStyle}
              disabled={!profile}
            >
              Request assessment
            </button>
            <button onClick={() => navigate(`/sm/assess/${userId}`)} style={buttonStyle}>
              Peer assessment
            </button>
          </div>
        }
      />
      <div style={{ padding: '24px', display: 'grid', gap: '32px' }}>
        {error && <div style={{ color: '#b91c1c' }}>{error}</div>}
        {!profile && !error && <div style={{ color: 'var(--color-dark-grey)' }}>Loading…</div>}
        {profile && (
          <>
            <CapabilityBadges
              capabilities={profile.capabilities}
              subjectLabel={profile.display_name}
              subjectUserId={profile.user_id}
              canManage={canManageBadges}
              onChanged={loadProfile}
            />

            <StrengthSpine skills={profile.skills} userId={profile.user_id} />

            {/* Feature #320 — one banner replaces the separate "Open deltas"
                and "Deltas in review" sections. Both are the same thing at
                different points in the lifecycle, and splitting them hid the
                R4 case where a skill carries several competing proposals. */}
            <OpenDeltaBanner
              deltas={profile.open_deltas}
              canReview
              selected={selectedDeltas}
              onToggle={toggleDelta}
              onOpenReview={handleOpenReview}
            />

            {/* Feature #326 — what has been asked, and of whom. */}
            <section>
              <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '4px' }}>
                Assessments requested
              </h2>
              <p
                style={{
                  fontSize: '13px',
                  color: 'var(--color-dark-grey)',
                  marginBottom: '12px',
                }}
              >
                What has been asked of colleagues about {profile.display_name}, and
                not yet returned.
              </p>
              {requests.length === 0 ? (
                <p style={{ color: 'var(--color-dark-grey)', fontSize: '14px' }}>
                  Nothing outstanding.
                </p>
              ) : (
                <div style={{ display: 'grid', gap: '10px' }}>
                  {requests.map((r) => (
                    <div
                      key={r.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '14px',
                        padding: '14px 16px',
                        border: '1px solid var(--color-light-grey)',
                        borderLeft: '4px solid #2563eb',
                        borderRadius: '8px',
                        fontSize: '14px',
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 500 }}>
                          {r.assessor_display_name}
                          {!r.show_current_ratings && (
                            <span
                              title="They are not shown the existing ratings — you asked for an independent view."
                              style={{
                                marginLeft: '8px',
                                fontSize: '11px',
                                color: 'var(--color-dark-grey)',
                                border: '1px solid var(--color-light-grey)',
                                borderRadius: '10px',
                                padding: '1px 9px',
                              }}
                            >
                              blind
                            </span>
                          )}
                        </div>
                        <div
                          style={{
                            fontSize: '12px',
                            color: 'var(--color-dark-grey)',
                            marginTop: '3px',
                          }}
                        >
                          {r.skills.map((s) => s.skill_name).join(', ') || 'no skills'}
                          {' · requested '}
                          {formatDateTime(r.requested_at)}
                          {r.requested_by_display_name &&
                            ` by ${r.requested_by_display_name}`}
                          {r.due_at && ` · due ${formatDateTime(r.due_at)}`}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRevoke(r)}
                        disabled={busyRequest === r.id}
                        style={{
                          ...secondaryButtonStyle,
                          padding: '7px 14px',
                          fontSize: '13px',
                          cursor: busyRequest === r.id ? 'wait' : 'pointer',
                          flexShrink: 0,
                        }}
                      >
                        Withdraw
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <AssessmentFeed
              history={profile.history}
              submissions={profile.submissions}
              reviews={profile.reviews}
            />
          </>
        )}
      </div>

      {showRequestDialog && profile && userId && (
        <RequestAssessmentDialog
          subjectUserId={userId}
          subjectDisplayName={profile.display_name}
          onClose={() => setShowRequestDialog(false)}
          onCreated={() => {
            setShowRequestDialog(false)
            loadRequests()
          }}
        />
      )}

      {/* Feature #344 — the warning. Proceed and Cancel, and nothing else:
          the manager is being told what they might be about to miss, not
          asked for permission. */}
      {outstanding && profile && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Assessments still outstanding"
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            zIndex: 50,
          }}
        >
          <div
            style={{
              backgroundColor: 'var(--color-white)',
              borderRadius: '12px',
              padding: '24px',
              width: '100%',
              maxWidth: '620px',
              display: 'grid',
              gap: '16px',
            }}
          >
            <h2 style={{ fontSize: '18px', fontWeight: 600, margin: 0 }}>
              {outstanding.length} assessment{outstanding.length === 1 ? '' : 's'} for{' '}
              {profile.display_name} {outstanding.length === 1 ? 'is' : 'are'} still
              outstanding
            </h2>
            <p
              style={{
                fontSize: '14px',
                color: 'var(--color-dark-grey)',
                margin: 0,
                lineHeight: 1.6,
              }}
            >
              Closing a review settles the deltas that exist right now. If any of
              these come back afterwards they may raise fresh deltas on the same
              skills, and you would be running a second review over ground you
              have just covered.
            </p>
            <ul
              style={{
                margin: 0,
                paddingLeft: '18px',
                fontSize: '13px',
                lineHeight: 1.8,
              }}
            >
              {outstanding.map((row) => (
                <li key={`${row.kind}-${row.id}`}>
                  <strong>{row.holder_display_name || 'Someone'}</strong>
                  {row.kind === 'draft'
                    ? ` has a ${row.assessment_type} assessment saved but not submitted`
                    : ' was asked to assess them'}
                  {row.kind === 'request' && row.skill_names.length > 0 && (
                    <> ({row.skill_names.join(', ')})</>
                  )}
                  {' — '}
                  {formatDateTime(row.occurred_at)}
                  {row.due_at && `, due ${formatDateTime(row.due_at)}`}
                </li>
              ))}
            </ul>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setOutstanding(null)}
                style={secondaryButtonStyle}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={openReview}
                disabled={openingReview}
                style={{
                  ...buttonStyle,
                  cursor: openingReview ? 'wait' : 'pointer',
                  opacity: openingReview ? 0.7 : 1,
                }}
              >
                {openingReview ? 'Opening…' : 'Proceed anyway'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default StaffDetail
